// 불꽃·반짝이 파티클 한 장짜리 캔버스(결과 화면의 CLEAR·카드 공개 연출용).
// 고정 크기 형 배열 풀 + 터질 때만 도는 requestAnimationFrame 한 줄. 프레임 안에서 새 객체를 만들지 않는다(AGENTS.md).
// 동시에 살아 있는 수는 capacity(저사양 폰은 ui/rewardCardsModel.particleBudget)가 상한이다. capacity 0이면 캔버스를 만들지 않고 아무 일도 하지 않는다.
const PALETTE = ['#fff6d2', '#ffd36b', '#ffb02e', '#ffffff'] as const;

export class SparkField {
  readonly canvas: HTMLCanvasElement | null = null;
  private readonly g: CanvasRenderingContext2D | null = null;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly life: Float32Array;
  private readonly max: Float32Array;
  private readonly size: Float32Array;
  private readonly col: Uint8Array;
  private readonly tint: string[];
  private live = 0;
  private raf = 0;
  private last = 0;
  private w = 0;
  private h = 0;
  /** QA: 지금까지 낸 파티클 수·동시에 살아 있던 최댓값 */
  spawned = 0;
  peak = 0;

  constructor(parent: HTMLElement, readonly capacity: number, tint?: readonly string[]) {
    const n = Math.max(0, capacity);
    this.x = new Float32Array(n); this.y = new Float32Array(n); this.vx = new Float32Array(n); this.vy = new Float32Array(n);
    this.life = new Float32Array(n); this.max = new Float32Array(n); this.size = new Float32Array(n); this.col = new Uint8Array(n);
    this.tint = tint ? tint.slice() : PALETTE.slice();
    if (n === 0) return;
    const c = document.createElement('canvas');
    c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
    c.setAttribute('aria-hidden', 'true');
    parent.appendChild(c);
    this.canvas = c;
    this.g = c.getContext('2d');
  }

  resize(w: number, h: number): void {
    if (!this.canvas) return;
    this.w = Math.max(1, Math.round(w)); this.h = Math.max(1, Math.round(h));
    if (this.canvas.width !== this.w) this.canvas.width = this.w;
    if (this.canvas.height !== this.h) this.canvas.height = this.h;
  }

  /** (x,y)에서 n개가 사방으로 터진다. 풀이 가득 차면 남는 몫은 버린다 */
  burst(x: number, y: number, n: number, speed: number, up = 0.35): void {
    if (!this.g) return;
    for (let k = 0; k < n && this.live < this.capacity; k++) {
      const i = this.free();
      if (i < 0) break;
      const a = Math.random() * Math.PI * 2, v = speed * (0.35 + Math.random() * 0.65);
      this.x[i] = x; this.y[i] = y;
      this.vx[i] = Math.cos(a) * v; this.vy[i] = Math.sin(a) * v - speed * up;
      this.max[i] = this.life[i] = 0.55 + Math.random() * 0.55;
      this.size[i] = 2 + Math.random() * 3.2;
      this.col[i] = (Math.random() * this.tint.length) | 0;
      this.live++; this.spawned++;
    }
    if (this.live > this.peak) this.peak = this.live;
    if (this.raf === 0 && this.live > 0) { this.last = 0; this.raf = requestAnimationFrame(this.frame); }
  }

  private free(): number {
    for (let i = 0; i < this.capacity; i++) if (this.life[i] <= 0) return i;
    return -1;
  }

  clear(): void {
    this.life.fill(0); this.live = 0;
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    if (this.g) this.g.clearRect(0, 0, this.w, this.h);
  }

  /** 화살표 함수 필드: 프레임마다 새 함수를 만들지 않는다 */
  private readonly frame = (t: number): void => {
    const g = this.g;
    if (!g) { this.raf = 0; return; }
    const dt = this.last === 0 ? 0.016 : Math.min(0.05, (t - this.last) / 1000);
    this.last = t;
    g.clearRect(0, 0, this.w, this.h);
    g.globalCompositeOperation = 'lighter';
    let alive = 0;
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) continue;
      this.vy[i] += 520 * dt; this.vx[i] *= 1 - 1.4 * dt;
      this.x[i] += this.vx[i] * dt; this.y[i] += this.vy[i] * dt;
      const k = this.life[i] / this.max[i];
      g.globalAlpha = k < 0.5 ? k * 2 : 1;
      g.fillStyle = this.tint[this.col[i]];
      const s = this.size[i] * (0.5 + k * 0.5);
      g.fillRect(this.x[i] - s / 2, this.y[i] - s / 2, s, s);
      alive++;
    }
    g.globalAlpha = 1;
    this.live = alive;
    if (alive > 0) this.raf = requestAnimationFrame(this.frame);
    else { this.raf = 0; g.clearRect(0, 0, this.w, this.h); }
  };
}
